import React, { useState } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ElectionService, CandidateService } from '../services/firebaseService';
import { Election, Candidate } from '../types';
import ImageUpload from './ImageUpload';
import ComboBox from './ComboBox';

interface CreateElectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateElectionModal: React.FC<CreateElectionModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Données de l'élection
  const [title, setTitle] = useState('');
  const [position, setPosition] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allowAbstention, setAllowAbstention] = useState(true);
  
  // Candidats
  const [candidates, setCandidates] = useState<Omit<Candidate, 'id'>[]>([
    { name: '', position: '', email: '', bio: '', photo: '', isActive: true, allowComments: false, commentRequired: false, commentLabel: '', commentPlaceholder: '' }
  ]);

  // Postes prédéfinis
  const predefinedPositions = [
    'Président(e)',
    'Vice-président(e) chargé(e) de la Stratégie',
    'Secrétaire Général(e)',
    'Trésorier(ère)',
    'Chargé(e) des Relations Extérieures et Communication',
    'Chargé(e) de la Formation et du Développement Professionnel',
    'Chargé(e) des Evénements et Partenariats',
    'Vice-président(e) chargé(e) des Opérations'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Valider les données
      if (!title || !position || !startDate || !endDate) {
        throw new Error('Tous les champs obligatoires doivent être remplis');
      }

      if (new Date(startDate) >= new Date(endDate)) {
        throw new Error('La date de fin doit être après la date de début');
      }

      const validCandidates = candidates.filter(c => c.name.trim() !== '');
      if (validCandidates.length === 0) {
        throw new Error('Au moins un candidat doit être ajouté');
      }

      // Créer les candidats d'abord
      const createdCandidates = [];
      for (const candidate of validCandidates) {
        const candidateData = {
          ...candidate,
          position,
          email: candidate.email || `${candidate.name.toLowerCase().replace(/\s+/g, '.')}@asprocom.com`
        };
        const candidateId = await CandidateService.createCandidate(candidateData);
        createdCandidates.push({
          id: candidateId,
          name: candidate.name,
          position,
          email: candidateData.email,
          bio: candidate.bio,
          photo: candidate.photo,
          isActive: true,
          allowComments: candidate.allowComments,
          commentRequired: candidate.commentRequired,
          commentLabel: candidate.commentLabel,
          commentPlaceholder: candidate.commentPlaceholder
        });
      }

      // Créer l'élection avec les candidats
      const electionData: Omit<Election, 'id'> = {
        title,
        position,
        description,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        isActive: true,
        allowAbstention,
        candidates: createdCandidates
      };

      await ElectionService.createElection(electionData);

      // Réinitialiser le formulaire
      setTitle('');
      setPosition('');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setCandidates([{ name: '', position: '', email: '', bio: '', photo: '', isActive: true }]);
      
      onSuccess();
      onClose();

    } catch (error: any) {
      setError(error.message || 'Erreur lors de la création de l\'élection');
    } finally {
      setLoading(false);
    }
  };

  const addCandidate = () => {
    setCandidates([...candidates, { name: '', position: '', email: '', bio: '', photo: '', isActive: true, allowComments: false, commentRequired: false, commentLabel: '', commentPlaceholder: '' }]);
  };

  const removeCandidate = (index: number) => {
    if (candidates.length > 1) {
      setCandidates(candidates.filter((_, i) => i !== index));
    }
  };

  const updateCandidate = (index: number, field: keyof Omit<Candidate, 'id'>, value: string | boolean) => {
    const updated = [...candidates];
    updated[index] = { ...updated[index], [field]: value };
    setCandidates(updated);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Créer une nouvelle élection</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Informations de base */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de l'élection *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-field"
                placeholder="Ex: Élection du Président"
                required
              />
            </div>

            <ComboBox
              value={position}
              onChange={setPosition}
              options={predefinedPositions}
              label="Poste"
              placeholder="Tapez ou sélectionnez un poste"
              required
              helpText="Tapez librement ou sélectionnez dans la liste déroulante"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-field"
              rows={3}
              placeholder="Description de l'élection..."
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début *
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin *
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field"
                required
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="allowAbstention"
              checked={allowAbstention}
              onChange={(e) => setAllowAbstention(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="allowAbstention" className="ml-2 text-sm text-gray-700">
              Permettre l'abstention pour cette élection
            </label>
          </div>

          {/* Candidats */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Candidats</h3>
              <button
                type="button"
                onClick={addCandidate}
                className="btn-secondary flex items-center"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Ajouter un candidat
              </button>
            </div>

            <div className="space-y-4">
              {candidates.map((candidate, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Candidat {index + 1}</h4>
                    {candidates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCandidate(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        value={candidate.name}
                        onChange={(e) => updateCandidate(index, 'name', e.target.value)}
                        className="input-field"
                        placeholder="Ex: Grace Monzele"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={candidate.email}
                        onChange={(e) => updateCandidate(index, 'email', e.target.value)}
                        className="input-field"
                        placeholder="grace.monzele@asprocom.com"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photo de profil
                    </label>
                    <ImageUpload
                      currentImageUrl={candidate.photo}
                      onImageUploaded={(url) => updateCandidate(index, 'photo', url)}
                      onImageRemoved={() => updateCandidate(index, 'photo', '')}
                      folder="candidates"
                      maxSize={5}
                      className="max-w-xs"
                    />
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Biographie (optionnel)
                    </label>
                    <textarea
                      value={candidate.bio}
                      onChange={(e) => updateCandidate(index, 'bio', e.target.value)}
                      className="input-field"
                      rows={2}
                      placeholder="Présentation du candidat..."
                    />
                  </div>

                  {/* Configuration des commentaires */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Configuration des commentaires (optionnel)</h5>
                    
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id={`allowComments-${index}`}
                        checked={candidate.allowComments || false}
                        onChange={(e) => updateCandidate(index, 'allowComments', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`allowComments-${index}`} className="ml-2 text-sm text-gray-700">
                        Permettre les commentaires pour ce choix
                      </label>
                    </div>

                    {candidate.allowComments && (
                      <>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Label du champ
                            </label>
                            <input
                              type="text"
                              value={candidate.commentLabel || ''}
                              onChange={(e) => updateCandidate(index, 'commentLabel', e.target.value)}
                              className="input-field"
                              placeholder="Ex: Justification, Pourquoi ce choix ?"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Placeholder (aide)
                            </label>
                            <input
                              type="text"
                              value={candidate.commentPlaceholder || ''}
                              onChange={(e) => updateCandidate(index, 'commentPlaceholder', e.target.value)}
                              className="input-field"
                              placeholder="Ex: Expliquez votre choix..."
                            />
                          </div>

                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`commentRequired-${index}`}
                              checked={candidate.commentRequired || false}
                              onChange={(e) => updateCandidate(index, 'commentRequired', e.target.checked)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`commentRequired-${index}`} className="ml-2 text-sm text-gray-700">
                              Commentaire obligatoire
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              {loading ? 'Création...' : 'Créer l\'élection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateElectionModal;